import { useEffect, useMemo } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { MapPin } from 'lucide-react';

import DateTimeCell from '@/shared/components/DateTimeCell';
import StatusBadge from '@/shared/components/StatusBadge';
import { cx } from '@/styles/muiTheme';
import { SessionData } from '@/types/user';

import useIPLookupQuery from '../hooks/useIPLookup';
import useAccountSessionsQuery from '../hooks/useListAccountSessions';

/**
 * Resolves one row's IP to a city. The lookup is a mutation rather than a query,
 * so it runs per row from an effect — the same shape ClipsTable's CameraCell
 * uses for its per-row fetch.
 */
const LocationCell = ({ ip }: { ip?: string }) => {
  const ipLookup = useIPLookupQuery();

  useEffect(() => {
    if (ip) ipLookup.mutate(ip);
  }, [ip]);

  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
      <MapPin size={14} color={cx.text.muted} />
      <span>
        {ipLookup.data ? `${ipLookup.data.city}, ${ipLookup.data.country_name}` : 'N/A'}
      </span>
    </Stack>
  );
};

const SessionList = () => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useAccountSessionsQuery();

  useEffect(() => {
    fetchNextPage();
  }, []);

  // The API is cursor-paginated and pages accumulate, so the table renders every
  // page fetched so far and "Load More" drives the cursor. MRT's own pagination
  // cannot express a cursor, which is why it stays disabled.
  const sessions = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data?.pages],
  );

  const columns = useMemo<MRT_ColumnDef<SessionData>[]>(
    () => [
      {
        id: 'createdAt',
        header: 'Created At',
        size: 150,
        accessorFn: (row) => row.sessionDetails?.createdAt,
        Cell: ({ row }) => <DateTimeCell value={row.original.sessionDetails?.createdAt} />,
      },
      {
        id: 'handler',
        header: 'CXVH Instance',
        size: 180,
        // Same field as the Handler column in Manage CXVH Sessions, so it reads
        // the same way here.
        muiTableBodyCellProps: { sx: { fontFamily: cx.fontMono } },
        accessorFn: (row) => row.sessionDetails?.handler ?? '',
        Cell: ({ row }) => row.original.sessionDetails?.handler || 'N/A',
      },
      {
        id: 'location',
        header: 'Location',
        size: 200,
        enableSorting: false,
        Cell: ({ row }) => <LocationCell ip={row.original.sessionDetails?.ip} />,
      },
      {
        id: 'status',
        header: 'Status',
        size: 120,
        accessorFn: (row) => (row.expired ? 'Expired' : 'Current'),
        Cell: ({ row }) => (
          <StatusBadge
            label={row.original.expired ? 'Expired' : 'Current'}
            // The hues the old solid Chip resolved error.main / success.main to.
            color={row.original.expired ? cx.red.main : cx.green}
          />
        ),
      },
    ],
    [],
  );

  return (
    <Box>
      <MaterialReactTable
        columns={columns}
        data={sessions}
        enablePagination={false}
        // Embedded panel rather than a page-level table: the search / filter /
        // density rail and the pagination footer would be chrome around a
        // handful of rows, and Load More below already owns paging.
        enableTopToolbar={false}
        enableBottomToolbar={false}
      />

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Button onClick={() => fetchNextPage()} disabled={!hasNextPage || isFetchingNextPage}>
          {isFetchingNextPage ? 'Loading more...' : 'Load More'}
        </Button>
      </Box>
    </Box>
  );
};

export default SessionList;
